import React, { MutableRefObject, ReactElement, useRef } from 'react'
import { Text, ThemeUIStyleObject } from 'theme-ui'
import { checkIfWithinInterval } from 'utils/getTimeSinceNow'
import { Flex } from '@theme-ui/components'
import Tippy from '@tippyjs/react'
import useGetColorMode from 'utils/useGetColorMode'
import { useHideTooltipOnScroll } from 'utils/useHideTooltipOnScroll'

export enum DeviceAnalyticsStatus {
  ONLINE, // used in last 60 seconds
  OFFLINE, // not used in last 60 seconds
  UNAVAILABLE, // status not available
}

export const deviceStatusToColor: Record<DeviceAnalyticsStatus, string> = {
  [DeviceAnalyticsStatus.ONLINE]: 'greenApple',
  [DeviceAnalyticsStatus.OFFLINE]: 'pomegranate',
  [DeviceAnalyticsStatus.UNAVAILABLE]: 'white',
}

export default function LastUsedIndicator({
  deviceStatus,
  deviceStatusBadgeText,
  lastActiveTimeStamp,
  sx,
}: {
  deviceStatus: DeviceAnalyticsStatus
  deviceStatusBadgeText?: string
  lastActiveTimeStamp?: number
  sx?: ThemeUIStyleObject
}): ReactElement {
  return (
    <Text
      data-testid="last-used-indicator"
      variant="size8Weight700"
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'absolute',
        left: '50%',
        top: '2rem',
        transform: 'translate(-50%, 0)',
        px: '0.4rem',
        height: '1.4rem',
        border: '1px solid',
        borderColor: 'darkBodyBG',
        color: 'darkBodyBG',
        borderRadius: '26px',
        backgroundColor: checkIfWithinInterval(lastActiveTimeStamp)
          ? 'banana'
          : deviceStatusToColor[deviceStatus],
        ...sx,
      }}
    >
      {deviceStatusBadgeText}
    </Text>
  )
}

export function LastUsedIndicatorTooltip({
  shouldShowTooltip,
  timeSinceLastQuery,
  children,
  scrollingContainerRef,
  appendTo,
}: {
  shouldShowTooltip: boolean
  timeSinceLastQuery?: string
  children: ReactElement
  scrollingContainerRef?: MutableRefObject<HTMLDivElement | null>
  appendTo?: Element | 'parent'
}): ReactElement {
  const { isLightMode } = useGetColorMode()
  const hideRef = useRef<() => void>()

  useHideTooltipOnScroll(hideRef, scrollingContainerRef)

  return (
    <Tippy
      onShow={(): void | false => {
        if (!shouldShowTooltip) {
          // returning false here stops the tooltip from showing
          return false
        }
      }}
      onCreate={instance => {
        hideRef.current = () => {
          instance.hide()
        }
      }}
      appendTo={appendTo ?? 'parent'}
      touch="hold"
      interactive={true}
      content={
        shouldShowTooltip ? (
          <Flex
            sx={{
              alignItems: 'center',
              width: '100%',
              textAlign: 'center',
              p: '0.4rem',
            }}
          >
            Last query <br />
            {timeSinceLastQuery}
          </Flex>
        ) : (
          <></>
        )
      }
      theme={isLightMode ? 'light-org-tooltip' : 'org-tooltip'}
      arrow={false}
      maxWidth={148}
      offset={[0, 5]}
    >
      {children}
    </Tippy>
  )
}
